import React, { useEffect, useState, useReducer, useCallback } from "react";

import {
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    makeStyles,
    InputAdornment,
    TextField,
    CircularProgress
} from "@material-ui/core";

import {
    DeleteOutline,
    Edit,
    Search
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import SubSpecialtyModal from "../../components/SubSpecialtyModal";

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    customTableCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_SUBSPECIALTIES") {
        const subSpecialties = action.payload;
        const newSubSpecialties = [];

        subSpecialties.forEach(subSpecialty => {
            const subSpecialtyIndex = state.findIndex(s => s.id === subSpecialty.id);
            if (subSpecialtyIndex !== -1) {
                state[subSpecialtyIndex] = subSpecialty;
            } else {
                newSubSpecialties.push(subSpecialty);
            }
        });

        return [...state, ...newSubSpecialties];
    }

    if (action.type === "UPDATE_SUBSPECIALTIES") {
        const subSpecialty = action.payload;
        const subSpecialtyIndex = state.findIndex(s => s.id === subSpecialty.id);

        if (subSpecialtyIndex !== -1) {
            state[subSpecialtyIndex] = subSpecialty;
            return [...state];
        } else {
            return [subSpecialty, ...state];
        }
    }

    if (action.type === "DELETE_SUBSPECIALTY") {
        const subSpecialtyId = action.payload;
        const subSpecialtyIndex = state.findIndex(s => s.id === subSpecialtyId);
        if (subSpecialtyIndex !== -1) {
            state.splice(subSpecialtyIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const SubSpecialties = () => {
    const classes = useStyles();

    const [subSpecialties, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [subSpecialtyModalOpen, setSubSpecialtyModalOpen] = useState(false);
    const [selectedSubSpecialty, setSelectedSubSpecialty] = useState(null);
    const [searchParam, setSearchParam] = useState("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingSubSpecialty, setDeletingSubSpecialty] = useState(null);

    useEffect(() => {
        dispatch({ type: "RESET" });
    }, [searchParam]);

    const fetchSubSpecialties = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/subspecialties", {
                params: { searchParam }
            });
            dispatch({ type: "LOAD_SUBSPECIALTIES", payload: data });
            setLoading(false);
        } catch (err) {
            toastError(err);
            setLoading(false);
        }
    }, [searchParam]);

    useEffect(() => {
        fetchSubSpecialties();
    }, [searchParam, fetchSubSpecialties]);

    const handleOpenSubSpecialtyModal = () => {
        setSubSpecialtyModalOpen(true);
        setSelectedSubSpecialty(null);
    };

    const handleCloseSubSpecialtyModal = () => {
        setSubSpecialtyModalOpen(false);
        setSelectedSubSpecialty(null);
    };

    const handleEditSubSpecialty = (subSpecialty) => {
        setSelectedSubSpecialty(subSpecialty);
        setSubSpecialtyModalOpen(true);
    };

    const handleDeleteSubSpecialty = async (subSpecialtyId) => {
        try {
            await api.delete(`/subspecialties/${subSpecialtyId}`);
            toast.success(i18n.t("subSpecialties.toasts.deleted"));
            dispatch({ type: "DELETE_SUBSPECIALTY", payload: subSpecialtyId });
        } catch (err) {
            toastError(err);
        }
        setDeletingSubSpecialty(null);
    };

    return (
        <MainContainer>
            <SubSpecialtyModal
                open={subSpecialtyModalOpen}
                onClose={handleCloseSubSpecialtyModal}
                subSpecialtyId={selectedSubSpecialty && selectedSubSpecialty.id}
                reload={fetchSubSpecialties}
            />
            <ConfirmationModal
                title={deletingSubSpecialty && `${i18n.t("subSpecialties.confirmationModal.deleteTitle")} ${deletingSubSpecialty.name}?`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteSubSpecialty(deletingSubSpecialty.id)}
            >
                {i18n.t("subSpecialties.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <MainHeader>
                <Title>{i18n.t("subSpecialties.title")}</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={(e) => setSearchParam(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="secondary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenSubSpecialtyModal}
                    >
                        {i18n.t("subSpecialties.buttons.add")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{i18n.t("subSpecialties.table.name")}</TableCell>
                                <TableCell align="center">{i18n.t("subSpecialties.table.specialty")}</TableCell>
                                <TableCell align="center">{i18n.t("subSpecialties.table.duration")}</TableCell>
                                <TableCell align="center">{i18n.t("subSpecialties.table.price")}</TableCell>
                                <TableCell align="center">{i18n.t("subSpecialties.table.actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <>
                                {subSpecialties.map((subSpecialty) => (
                                    <TableRow key={subSpecialty.id}>
                                        <TableCell align="center">{subSpecialty.name}</TableCell>
                                        <TableCell align="center">{subSpecialty.specialty ? subSpecialty.specialty.name : ""}</TableCell>
                                        <TableCell align="center">{subSpecialty.duration}</TableCell>
                                        <TableCell align="center">{subSpecialty.price}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditSubSpecialty(subSpecialty)}
                                            >
                                                <Edit />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDeletingSubSpecialty(subSpecialty);
                                                    setConfirmModalOpen(true);
                                                }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {loading && <TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>}
                            </>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default SubSpecialties;
